import { useState, useEffect } from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import { DiscountModifier } from '../shared/DiscountModifier';
import { useBandwidthCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FieldTooltip } from '../shared/FieldTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';

// BandwidthCalc.tsx
// Refactored using established Stitch design patterns
// Converted from Stitch screen: desktop_bandwidth_view/code.html

export default function BandwidthCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { bandwidthInputs, setBandwidthInputs, discount, setDiscount } = useCalculatorStore();
  const { data: calculation, isLoading, isError } = useBandwidthCalc(bandwidthInputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (calculation?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'bandwidth', result_gbp: calculation.monthly_estimate })
    }
  }, [calculation?.monthly_estimate])

  const handleInternetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const outboundInternetGB = parseInt(e.target.value);
    setBandwidthInputs({ outboundInternetGB });
  };

  const handleInterRegionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const interRegionGB = parseInt(e.target.value);
    setBandwidthInputs({ interRegionGB });
  };

  return (
    <div className={`max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      {/* Left Column: Config */}
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Small SaaS',
            description: '500 GB outbound internet — shows tiered pricing above 100 GB free',
            apply: () => setBandwidthInputs({ outboundInternetGB: 500, interRegionGB: 0 }),
          },
          {
            label: 'Video / media streaming',
            description: '10 TB outbound — CDN-level egress volume',
            apply: () => setBandwidthInputs({ outboundInternetGB: 10000, interRegionGB: 0 }),
          },
          {
            label: 'Multi-region app',
            description: '1 TB outbound + 2 TB inter-region replication',
            apply: () => setBandwidthInputs({ outboundInternetGB: 1000, interRegionGB: 2000 }),
          },
        ]} />
        {/* Outbound Internet Card */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-[#bc5b00]" style={{ fontVariationSettings: "'FILL' 1" }}>public</span>
              Outbound Data Transfer
            </h2>
            <span className="text-[10px] font-bold text-[#107c10] bg-[#107c10]/10 px-2 py-0.5 border border-[#107c10]/30 flex items-center gap-1 uppercase">
              <span className="material-symbols-outlined text-[12px]">check_circle</span>
              FIRST 100GB FREE
            </span>
          </div>
          
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Internet Egress <FieldTooltip content="Data transferred out from Azure to the public internet. First 100 GB/month is free; tiered pricing applies above that." /></label>
              <span className="text-xl font-bold font-mono text-on-surface">{bandwidthInputs.outboundInternetGB?.toLocaleString()} GB</span>
            </div>
            <input 
              className="w-full h-1 rounded-lg appearance-none cursor-pointer accent-primary" style={{ backgroundColor: 'var(--slider-track)' }} 
              max="5000" 
              min="0" 
              step="1"
              type="range" 
              value={bandwidthInputs.outboundInternetGB || 0}
              onChange={handleInternetChange}
            />
            <p className="text-[10px] text-outline italic">Pricing based on Routing Preference: Microsoft Premium Network</p>
          </div>
        </div>

        {/* Inter-Region Card */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>swap_calls</span>
              Inter-Region Transfer
            </h2>
          </div>
          
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Region-to-Region <FieldTooltip content="Data transferred between different Azure regions (e.g., UK South to West Europe). Charged per GB at inter-region rates." /></label>
              <span className="text-xl font-bold font-mono text-on-surface">{bandwidthInputs.interRegionGB?.toLocaleString()} GB</span>
            </div>
            <input 
              className="w-full h-1 rounded-lg appearance-none cursor-pointer accent-primary" style={{ backgroundColor: 'var(--slider-track)' }} 
              max="5000" 
              min="0" 
              step="1"
              type="range" 
              value={bandwidthInputs.interRegionGB || 0}
              onChange={handleInterRegionChange}
            />
            <p className="text-[10px] text-outline italic">Flat rate pricing for transfers within the same continental area (e.g. Europe).</p>
            <div className="mt-3 p-3 bg-surface-container-lowest/60 border border-outline-variant/50 rounded-sm flex items-start gap-2">
              <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">info</span>
              <p className="text-[11px] text-outline leading-snug italic">
                Inter-Availability Zone transfer within UK South is free (updated 2023).
              </p>
            </div>
          </div>
        </div>

        {/* Private Endpoint note */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-outline text-[18px] shrink-0 mt-0.5">lock</span>
          <p className="text-[11px] text-outline leading-relaxed italic">
            Traffic through Private Endpoints incurs Private Link data processing charges (~£0.01/GB) in addition to any egress costs.
          </p>
        </div>

        {/* Ingress Note */}
        <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-4 flex gap-4 items-start rounded-sm">
          <span className="material-symbols-outlined text-[#107c10]">info</span>
          <div>
            <p className="text-sm text-on-surface leading-tight">
              <span className="font-bold text-[#107c10]">Ingress (Inbound) is FREE.</span> All data coming into Azure data centers from the internet or other regions is not charged.
            </p>
          </div>
        </div>
      </div>

      {/* Right Column: Results */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {/* Results Card */}
          <div className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden flex flex-col">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${calculation?.monthly_estimate.toFixed(2)}`}
                </span>
                {!isLoading && !isError && calculation && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              <p className="text-[10px] text-outline mt-1 italic font-mono uppercase tracking-widest">
                UK South · Pay-As-You-Go
              </p>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {calculation?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">{item.label}</span>
                    <span className="font-bold">£{item.value.toFixed(2)}</span>
                  </li>
                ))}
                {!calculation && !isLoading && (
                  <p className="text-[10px] text-outline text-center py-4">Adjust configuration to see pricing</p>
                )}
                {calculation && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{calculation.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>
            {calculation && (
              <div className="px-6 pb-2">
                <FormulaDisclosure
                  lines={[
                    ...calculation.breakdown.map((item) => `${item.label}: £${item.value.toFixed(4)}`),
                    `Outbound: ${bandwidthInputs.outboundInternetGB ?? 0}GB · Inter-region: ${bandwidthInputs.interRegionGB ?? 0}GB · Region: UK South`,
                  ]}
                  fetchedAt={calculation.prices_last_updated}
                />
              </div>
            )}
            <div className="p-4 bg-surface-container-highest flex gap-2">
              <button className="flex-1 bg-primary text-on-primary py-2.5 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                <span className="material-symbols-outlined text-sm">share</span>
                Share
              </button>
              <button className="w-12 bg-surface-container-high border border-outline-variant flex items-center justify-center hover:bg-surface-container-highest transition-colors">
                <span className="material-symbols-outlined">save</span>
              </button>
            </div>
          </div>

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 flex gap-3">
            <span className="material-symbols-outlined text-outline text-lg">info</span>
            <p className="text-[10px] text-outline leading-relaxed italic">
              {calculation?.disclaimer || "Values are estimates based on standard retail rates. Volume discounts may apply for transfers exceeding 10TB/month."}
            </p>
          </div>
          {calculation && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={calculation.monthly_estimate}
            />
          )}
          <div className="px-0 pb-2">
            <ShareButton inputs={bandwidthInputs} result={calculation ?? null} service="bandwidth" />
          </div>
          <ResourcesSection service="bandwidth" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="bandwidth"
          estimateValue={calculation ? `£${calculation.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
